/**
 * Blog and language path helpers. Used by Header and any component that needs
 * to detect blog routes or build blog language-switch URLs.
 */

/** Home: Finnish (root – default). */
export const HOME_FI = '/';
/** Home: English. */
export const HOME_EN = '/en';
/** Base path for English blog. */
export const BLOG_EN = '/blog';
/** Base path for Finnish blog. */
export const BLOG_FI = '/blogi';
/** Base path for English work/case studies list. */
export const WORK_EN = '/work';
/** Base path for Finnish work/case studies list (Työmme). */
export const WORK_FI = '/tyomme';
/** About page: English. */
export const ABOUT_EN = '/about';
/** About page: Finnish (Meistä). */
export const ABOUT_FI = '/meista';
/** Team page: English. */
export const TEAM_EN = '/team';
/** Team page: Finnish (Tiimi). */
export const TEAM_FI = '/tiimi';
/** Contact page: English. */
export const CONTACT_EN = '/contact';
/** Contact page: Finnish (Yhteys). */
export const CONTACT_FI = '/yhteys';
/** Careers page: English. */
export const CAREERS_EN = '/careers';
/** Careers page: Finnish (Rekry). */
export const CAREERS_FI = '/rekry';
/** Services page: English. */
export const SERVICES_EN = '/services';
/** Services page: Finnish (Palvelut). */
export const SERVICES_FI = '/palvelut';
/** Privacy: Finnish (Tietosuojaseloste). */
export const PRIVACY_FI = '/tietosuojaseloste';
/** Privacy: English (root path to avoid /en/ routing conflicts). */
export const PRIVACY_EN = '/privacy-policy';

/**
 * Base path for services in the given language.
 * @param {'en'|'fi'} lang
 * @returns {string}
 */
export function getServicesBasePath(lang) {
  return lang === 'fi' ? SERVICES_FI : SERVICES_EN;
}

/**
 * Subpath for a services subpage (software-development | ui-ux-design).
 * @param {'en'|'fi'} lang
 * @param {'software-development'|'ui-ux-design'} slug - English slug.
 * @returns {string} e.g. /palvelut/ohjelmistokehitys or /services/software-development
 */
export function getServicesSubpath(lang, slug) {
  const base = getServicesBasePath(lang);
  if (lang === 'fi') {
    if (slug === 'software-development') return `${base}/ohjelmistokehitys`;
    if (slug === 'ui-ux-design') return `${base}/ui-ux-suunnittelu`;
  }
  return `${base}/${slug}`;
}

/**
 * Returns the blog list URL for the target language.
 * @param {string} [pathname] - Current pathname (e.g. router.pathname).
 * @param {'en'|'fi'} targetLang - Target language.
 * @returns {string} - /blog or /blogi.
 */
export function getBlogLanguageSwitchUrl(pathname, targetLang) {
  if (!pathname) return targetLang === 'fi' ? BLOG_FI : BLOG_EN;
  if (pathname === BLOG_EN || pathname.startsWith(`${BLOG_EN}/`) || pathname === BLOG_FI || pathname.startsWith(`${BLOG_FI}/`)) {
    return targetLang === 'fi' ? BLOG_FI : BLOG_EN;
  }
  return targetLang === 'fi' ? BLOG_FI : BLOG_EN;
}

/** True when the current path is the blog list (EN or FI). */
export function isOnBlogPage(pathname) {
  return pathname === BLOG_EN || pathname?.startsWith(`${BLOG_EN}/`) || pathname === BLOG_FI || pathname?.startsWith(`${BLOG_FI}/`);
}

/** True when the current route is a blog post (dynamic [slug]). */
export function isBlogPostPage(pathname) {
  return pathname === `${BLOG_EN}/[slug]` || pathname === `${BLOG_FI}/[slug]`;
}

/** True when the current page is the Finnish version (e.g. /etusivu, /blogi, /tyomme, /meista, /tiimi, /yhteys, /rekry, /palvelut, /tietosuojaseloste). */
export function isFinnishPage(pathname) {
  return (
    pathname === HOME_FI ||
    pathname === BLOG_FI ||
    pathname?.startsWith(`${BLOG_FI}/`) ||
    pathname === WORK_FI ||
    pathname?.startsWith(`${WORK_FI}/`) ||
    pathname === ABOUT_FI ||
    pathname === TEAM_FI ||
    pathname === CONTACT_FI ||
    pathname === CAREERS_FI ||
    pathname?.startsWith(`${CAREERS_FI}/`) ||
    pathname === SERVICES_FI ||
    pathname?.startsWith(`${SERVICES_FI}/`) ||
    pathname === PRIVACY_FI ||
    pathname?.startsWith(`${PRIVACY_FI}/`)
  );
}

/**
 * Language from current URL path (asPath). Use for syncing context and cookies.
 * Handles dynamic routes (e.g. /meista vs /about) via asPath.
 * @param {string} asPath - router.asPath (e.g. /etusivu, /en/blog, /meista).
 * @returns {'fi'|'en'}
 */
export function getLangFromPath(asPath) {
  if (!asPath || typeof asPath !== 'string') return 'fi';
  const path = asPath.split('?')[0];
  if (path === '/') return 'fi';
  if (path === '/en' || path.startsWith('/en/')) return 'en';
  if (path === '/etusivu' || path.startsWith('/etusivu/')) return 'fi';
  if (path === BLOG_FI || path.startsWith(`${BLOG_FI}/`)) return 'fi';
  if (path === WORK_FI || path.startsWith(`${WORK_FI}/`)) return 'fi';
  if (path === ABOUT_FI || path.startsWith(`${ABOUT_FI}/`)) return 'fi';
  if (path === TEAM_FI || path.startsWith(`${TEAM_FI}/`)) return 'fi';
  if (path === CONTACT_FI || path.startsWith(`${CONTACT_FI}/`)) return 'fi';
  if (path === CAREERS_FI || path.startsWith(`${CAREERS_FI}/`)) return 'fi';
  if (path === SERVICES_FI || path.startsWith(`${SERVICES_FI}/`)) return 'fi';
  if (path === PRIVACY_FI || path.startsWith(`${PRIVACY_FI}/`)) return 'fi';
  return 'en';
}
